export const PermissionTypes = {
  GET_PERMISSION_CONSTANTS_REQUEST: "GET_PERMISSION_CONSTANTS_REQUEST",
  GET_PERMISSION_CONSTANTS_SUCCESS: "GET_PERMISSION_CONSTANTS_SUCCESS",
  GET_PERMISSION_CONSTANTS_FAILURE: "GET_PERMISSION_CONSTANTS_FAILURE",

  GET_PERMISSION_SCOPE_OPTIONS_REQUEST: "GET_PERMISSION_SCOPE_OPTIONS_REQUEST",
  GET_PERMISSION_SCOPE_OPTIONS_SUCCESS: "GET_PERMISSION_SCOPE_OPTIONS_SUCCESS",
  GET_PERMISSION_SCOPE_OPTIONS_FAILURE: "GET_PERMISSION_SCOPE_OPTIONS_FAILURE",

  GET_PERMISSION_SCOPE_PROGRAMS_REQUEST: "GET_PERMISSION_SCOPE_PROGRAMS_REQUEST",
  GET_PERMISSION_SCOPE_PROGRAMS_SUCCESS: "GET_PERMISSION_SCOPE_PROGRAMS_SUCCESS",
  GET_PERMISSION_SCOPE_PROGRAMS_FAILURE: "GET_PERMISSION_SCOPE_PROGRAMS_FAILURE",

  GET_PERMISSION_ME_REQUEST: "GET_PERMISSION_ME_REQUEST",
  GET_PERMISSION_ME_SUCCESS: "GET_PERMISSION_ME_SUCCESS",
  GET_PERMISSION_ME_FAILURE: "GET_PERMISSION_ME_FAILURE",

  GET_PERMISSION_ROLES_REQUEST: "GET_PERMISSION_ROLES_REQUEST",
  GET_PERMISSION_ROLES_SUCCESS: "GET_PERMISSION_ROLES_SUCCESS",
  GET_PERMISSION_ROLES_FAILURE: "GET_PERMISSION_ROLES_FAILURE",

  UPSERT_PERMISSION_ROLE_REQUEST: "UPSERT_PERMISSION_ROLE_REQUEST",
  UPSERT_PERMISSION_ROLE_SUCCESS: "UPSERT_PERMISSION_ROLE_SUCCESS",
  UPSERT_PERMISSION_ROLE_FAILURE: "UPSERT_PERMISSION_ROLE_FAILURE",

  DELETE_PERMISSION_ROLE_REQUEST: "DELETE_PERMISSION_ROLE_REQUEST",
  DELETE_PERMISSION_ROLE_SUCCESS: "DELETE_PERMISSION_ROLE_SUCCESS",
  DELETE_PERMISSION_ROLE_FAILURE: "DELETE_PERMISSION_ROLE_FAILURE",

  CHANGE_PERMISSION_ROLE_STATUS_REQUEST: "CHANGE_PERMISSION_ROLE_STATUS_REQUEST",
  CHANGE_PERMISSION_ROLE_STATUS_SUCCESS: "CHANGE_PERMISSION_ROLE_STATUS_SUCCESS",
  CHANGE_PERMISSION_ROLE_STATUS_FAILURE: "CHANGE_PERMISSION_ROLE_STATUS_FAILURE",

  GET_PERMISSION_ASSIGNMENTS_REQUEST: "GET_PERMISSION_ASSIGNMENTS_REQUEST",
  GET_PERMISSION_ASSIGNMENTS_SUCCESS: "GET_PERMISSION_ASSIGNMENTS_SUCCESS",
  GET_PERMISSION_ASSIGNMENTS_FAILURE: "GET_PERMISSION_ASSIGNMENTS_FAILURE",

  UPSERT_PERMISSION_ASSIGNMENT_REQUEST: "UPSERT_PERMISSION_ASSIGNMENT_REQUEST",
  UPSERT_PERMISSION_ASSIGNMENT_SUCCESS: "UPSERT_PERMISSION_ASSIGNMENT_SUCCESS",
  UPSERT_PERMISSION_ASSIGNMENT_FAILURE: "UPSERT_PERMISSION_ASSIGNMENT_FAILURE",

  DELETE_PERMISSION_ASSIGNMENTS_REQUEST: "DELETE_PERMISSION_ASSIGNMENTS_REQUEST",
  DELETE_PERMISSION_ASSIGNMENTS_SUCCESS: "DELETE_PERMISSION_ASSIGNMENTS_SUCCESS",
  DELETE_PERMISSION_ASSIGNMENTS_FAILURE: "DELETE_PERMISSION_ASSIGNMENTS_FAILURE",

  CHANGE_PERMISSION_ASSIGNMENT_STATUS_REQUEST: "CHANGE_PERMISSION_ASSIGNMENT_STATUS_REQUEST",
  CHANGE_PERMISSION_ASSIGNMENT_STATUS_SUCCESS: "CHANGE_PERMISSION_ASSIGNMENT_STATUS_SUCCESS",
  CHANGE_PERMISSION_ASSIGNMENT_STATUS_FAILURE: "CHANGE_PERMISSION_ASSIGNMENT_STATUS_FAILURE",
};

// ─── Constants API Response Types ──────────────────────────────────────────
export interface PermissionConstantsData {
  systems: string[];
  factory_operations: string[];
  feature_actions: Record<string, Record<string, Array<Record<string, string>>>>;
}

// ─── Scope Options API Response Types ─────────────────────────────────────
export interface CmLocationOption {
  cm_location_code: string;
  cm: string;
  cm_location: string;
}

export interface PermissionScopeOptionsData {
  cm_locations_options: CmLocationOption[];
}

export interface PermissionScopeProgramsData {
  programs: string[];
}

// ─── API Response Types ─────────────────────────────────────────────────────
export interface PermissionRolePermission {
  feature: string;
  actions: string[];
}

export interface PermissionRole {
  role_id: string;
  system: string;
  role_name: string;
  type: "system" | "custom";
  description: string;
  permissions: PermissionRolePermission[];
  creator: string;
  status: string;
  version: number;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export type PermissionRolesData = PermissionRole[];

export interface PermissionRolesResponse {
  status: string;
  data: PermissionRolesData;
}

// ─── Assignment API Types ───────────────────────────────────────────────────
export type EntityType = "user" | "team" | "vendor";

export interface CmLocation {
  cm: string;
  location: string[];
}

export interface PermissionAssignment {
  assignment_id: string;
  system: string;
  entity_type: EntityType;
  entity: string;
  roles: string[];
  operations: string[];
  cm_locations: CmLocation[];
  programs: string[];
  status: "active" | "inactive";
  version: number;
  created_by: string;
  assigned_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface PermissionAssignmentsData {
  assignments: PermissionAssignment[];
  total_count?: number;
}

// ─── Permission Me (Current User Access) Types ─────────────────────────────
export interface UserPermissionFeature {
  feature: string;
  actions: string[];
}

export interface MeAssignmentDataScope {
  operations: string[];
  cm_locations: CmLocation[];
  programs: string[];
}

export interface MeAssignment {
  assignment_id: string;
  system: string;
  roles: string[];
  permissions: UserPermissionFeature[];
  data_scope: MeAssignmentDataScope;
}

export interface PermissionMeResponse {
  user: string;
  is_system_admin: Record<string, boolean>;
  system_admin_systems: string[];
  assignments: MeAssignment[];
  assignable_roles: string[];
}

// ─── Redux State ────────────────────────────────────────────────────────────
export type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

export interface PermissionState {
  constants: {
    data: PermissionConstantsData | null;
    status: RequestStatus;
    error: string | null;
  };
  scopeOptions: {
    data: PermissionScopeOptionsData | null;
    status: RequestStatus;
    error: string | null;
  };
  scopePrograms: {
    data: PermissionScopeProgramsData | null;
    status: RequestStatus;
    error: string | null;
  };
  me: {
    data: PermissionMeResponse | null;
    status: RequestStatus;
    error: string | null;
  };
  roles: {
    data: PermissionRolesData | null;
    status: RequestStatus;
    error: string | null;
  };
  upsertRole: {
    status: RequestStatus;
    error: string | null;
  };
  deleteRole: {
    status: RequestStatus;
    error: string | null;
  };
  changeRoleStatus: {
    status: RequestStatus;
    error: string | null;
  };
  assignments: {
    data: PermissionAssignmentsData | null;
    status: RequestStatus;
    error: string | null;
  };
  upsertAssignment: {
    status: RequestStatus;
    error: string | null;
  };
  deleteAssignment: {
    status: RequestStatus;
    error: string | null;
  };
  changeAssignmentStatus: {
    status: RequestStatus;
    error: string | null;
  };
}
